import { Flex, Text } from '@radix-ui/themes';
import {
  BetaIcon,
  SuperAdminIcon,
} from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSwitchInput } from 'components/common/form/switch';
import { SettingForm } from 'components/common/settings-dialog/form';
import { InactiveCallout } from 'components/common/settings-dialog/inactive-callout';
import { SettingRow } from 'components/common/settings-dialog/row';
import { MachineContext } from 'contexts/machine.context';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { t } from 'i18next';
import { SEAMLESS_BALL_TYPES } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  FF_NAME_ON_DEMAND_MODE,
  TrackingDevice,
} from 'lib_ts/enums/training.enums';
import { useContext } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

export const MachineFiringTab = () => {
  const onDemand = useFeatureFlag(FF_NAME_ON_DEMAND_MODE);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const { rapidMode, isActive, setRapidMode, machine, update } =
    useContext(MachineContext);

  const showRapidMode =
    userRoleFeatureFlags.controls.rapidMode ||
    SEAMLESS_BALL_TYPES.includes(machine.ball_type);

  const showDeceptionMode = !!machine.feature_flags?.jitter_mode;

  const canUseOnDemand =
    machine.tracking_device === TrackingDevice.TrajektVision;

  return (
    <ErrorBoundary identifier="MachineFiringTab">
      <SettingForm>
        {!isActive && <InactiveCallout />}
        {showRapidMode && (
          <SettingRow
            header="settings.rapid-mode"
            description={
              <Text>
                {t(
                  rapidMode
                    ? 'settings.rapid-mode-disable-msg'
                    : 'settings.rapid-mode-enable-msg'
                )}
              </Text>
            }
            input={
              <Flex justify="end">
                <CommonSwitchInput
                  id="firing-rapid-mode"
                  defaultChecked={rapidMode}
                  onCheckedChange={setRapidMode}
                  disabled={!isActive}
                />
              </Flex>
            }
          />
        )}

        <SettingRow
          separatorBefore={showRapidMode}
          header="settings.tracking-device-validation"
          // todo: fix the header to match reality once CS is ready with doc updates
          // header="settings.confidence-checks"
          description={
            <Text>
              {t(
                machine.enable_confidence_checks
                  ? 'settings.disable-confidence-checks-msg'
                  : 'settings.enable-confidence-checks-msg'
              )}
            </Text>
          }
          input={
            <Flex justify="end">
              <CommonSwitchInput
                id="firing-confidence-checks"
                defaultChecked={machine.enable_confidence_checks}
                onCheckedChange={(v) => {
                  update({
                    _id: machine._id,
                    enable_confidence_checks: v,
                  });
                }}
                disabled={!isActive}
              />
            </Flex>
          }
        />

        {onDemand && (
          // ? only show the control as read-only and enabled while it is actually enabled via platform console
          <SettingRow
            separatorBefore
            header="On-Demand Pitches"
            headerDecoration={<SuperAdminIcon />}
            description={
              <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
                <Text color={RADIX.COLOR.WARNING}>
                  Description of on-demand pitching mode goes here. Comments
                  about safety, be careful, etc...
                </Text>

                {!canUseOnDemand && (
                  <Text color={RADIX.COLOR.HINT} size="1">
                    This feature is only available while using Trajekt Vision.
                  </Text>
                )}
              </Flex>
            }
            input={
              <Flex justify="end">
                <CommonSwitchInput
                  id="firing-on-demand-pitches"
                  defaultChecked={onDemand}
                  onCheckedChange={() => {
                    // do nothing
                  }}
                  disabled
                />
              </Flex>
            }
          />
        )}

        {showDeceptionMode && (
          <SettingRow
            separatorBefore
            header="settings.deception-mode"
            headerDecoration={<BetaIcon />}
            description={
              <Text>
                {t(
                  machine.enable_jitter
                    ? 'settings.deception-mode-disable-msg'
                    : 'settings.deception-mode-enable-msg'
                )}
              </Text>
            }
            input={
              <Flex justify="end">
                <CommonSwitchInput
                  id="firing-deception-mode"
                  defaultChecked={!!machine.enable_jitter}
                  onCheckedChange={(v) =>
                    update({ _id: machine._id, enable_jitter: !!v })
                  }
                  disabled={!isActive}
                />
              </Flex>
            }
          />
        )}
      </SettingForm>
    </ErrorBoundary>
  );
};
